import React, { useState, useEffect } from'react'
import { useNavigate } from'react-router-dom'
import { Shield, Copy, CheckCircle2, Loader2, AlertTriangle, Key, ArrowLeft, ShieldCheck } from'lucide-react'
import { Button } from'../components/ui/Button'
import { DEFAULT_APP_HOME } from'../config/routes'

interface SetupData {
 qr_code: string
 manual_entry_key: string
 backup_codes: string[]
}

export const MFASetup: React.FC = () => {
 const navigate = useNavigate()
 const [step, setStep] = useState<'checking' |'already-enabled' |'intro' |'setup' |'verify' |'backup' |'complete'>('checking')
 const [loading, setLoading] = useState(false)
 const [error, setError] = useState('')
 const [setupData, setSetupData] = useState<SetupData | null>(null)
 const [verificationCode, setVerificationCode] = useState('')
 const [copiedKey, setCopiedKey] = useState(false)
 const [copiedCodes, setCopiedCodes] = useState(false)

 // Check if MFA is already enabled
 useEffect(() => {
 const checkMfaStatus = async () => {
 try {
 const response = await fetch('/api/mfa/status', { credentials:'include' })
 if (response.ok) {
 const data = await response.json()
 if (data.mfa_enabled) {
 setStep('already-enabled')
 } else {
 setStep('intro')
 }
 } else {
 setStep('intro')
 }
 } catch {
 setStep('intro')
 }
 }
 checkMfaStatus()
 }, [])

 const startSetup = async () => {
 setLoading(true)
 setError('')

 try {
 const response = await fetch('/api/mfa/setup', {
 method:'POST',
 credentials:'include'
 })

 const data = await response.json()

 if (data.success) {
 setSetupData(data)
 setStep('setup')
 } else {
 setError(data.error ||'Failed to start MFA setup')
 }
 } catch (err) {
 setError('An error occurred. Please try again.')
 } finally {
 setLoading(false)
 }
 }

 const verifyCode = async () => {
 if (verificationCode.length !== 6) {
 setError('Please enter a 6-digit code')
 return
 }

 setLoading(true)
 setError('')

 try {
 const response = await fetch('/api/mfa/verify-setup', {
 method:'POST',
 headers: {'Content-Type':'application/json' },
 credentials:'include',
 body: JSON.stringify({ code: verificationCode })
 })

 const data = await response.json()

 if (data.success) {
 setStep('backup')
 } else {
 setError(data.error ||'Invalid code')
 }
 } catch (err) {
 setError('Verification failed. Please try again.')
 } finally {
 setLoading(false)
 }
 }

 const copyToClipboard = async (text: string, type:'key' |'codes') => {
 await navigator.clipboard.writeText(text)
 if (type ==='key') {
 setCopiedKey(true)
 setTimeout(() => setCopiedKey(false), 2000)
 } else {
 setCopiedCodes(true)
 setTimeout(() => setCopiedCodes(false), 2000)
 }
 }

 const formatBackupCodes = () => {
 return setupData?.backup_codes.join('\n') ||''
 }

 // Checking step
 if (step ==='checking') {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="text-center">
 <Loader2 className="w-8 h-8 animate-spin text-brand mx-auto mb-4" />
 <p className="text-secondary">Checking MFA status...</p>
 </div>
 </div>
 )
 }

 // Already enabled step
 if (step ==='already-enabled') {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-lg">
 <div className="bg-surface rounded-2xl shadow-xl p-8">
 <div className="text-center mb-8">
 <div className="w-20 h-20 bg-ok rounded-2xl flex items-center justify-center mx-auto mb-6">
 <ShieldCheck className="w-10 h-10 text-on-brand" />
 </div>
 <h1 className="text-xl font-semibold text-primary mb-2">
 Two-Factor Authentication Already Enabled
 </h1>
 <p className="text-secondary">
 Your account is already protected with 2FA. Great job keeping your data secure!
 </p>
 </div>

 <div className="bg-ok/10 border border-ok/30 rounded-lg p-4 mb-6">
 <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-ok" />
 <span className="text-ok font-medium">
 Two-factor authentication is active
 </span>
 </div>
 </div>

 <div className="flex gap-3">
 <Button
 onClick={() => navigate('/')}
 className="flex-1 bg-ok hover:opacity-90 text-on-brand"
 >
 Go to Dashboard
 </Button>
 <Button
 variant="outline"
 onClick={() => navigate('/profile?tab=security')}
 className="flex-1"
 >
 Security Settings
 </Button>
 </div>
 </div>
 </div>
 </div>
 )
 }

 // Intro step
 if (step ==='intro') {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-lg">
 <div className="bg-surface rounded-2xl shadow-xl p-8">
 <div className="text-center mb-8">
 <div className="w-20 h-20 bg-ok rounded-2xl flex items-center justify-center mx-auto mb-6">
 <Shield className="w-10 h-10 text-on-brand" />
 </div>
 <h1 className="text-xl font-semibold text-primary mb-2">
 Enable Two-Factor Authentication
 </h1>
 <p className="text-secondary">
 Add an extra layer of security to your account
 </p>
 </div>

 <div className="space-y-4 mb-8">
 <div className="flex items-start gap-4 p-4 bg-surface-alt rounded-xl">
 <div className="w-8 h-8 bg-brand-subtle rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
 <span className="text-brand font-bold text-sm">1</span>
 </div>
 <div>
 <p className="font-medium text-primary">Download an authenticator app</p>
 <p className="text-sm text-secondary">
 Use Google Authenticator, Authy, or any TOTP app
 </p>
 </div>
 </div>

 <div className="flex items-start gap-4 p-4 bg-surface-alt rounded-xl">
 <div className="w-8 h-8 bg-brand-subtle rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
 <span className="text-brand font-bold text-sm">2</span>
 </div>
 <div>
 <p className="font-medium text-primary">Scan the QR code</p>
 <p className="text-sm text-secondary">
 Or manually enter the setup key
 </p>
 </div>
 </div>

 <div className="flex items-start gap-4 p-4 bg-surface-alt rounded-xl">
 <div className="w-8 h-8 bg-brand-subtle rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
 <span className="text-brand font-bold text-sm">3</span>
 </div>
 <div>
 <p className="font-medium text-primary">Enter verification code</p>
 <p className="text-sm text-secondary">
 Confirm setup with a code from your app
 </p>
 </div>
 </div>
 </div>

 {error && (
 <div className="mb-4 p-4 bg-bad/10 border border-bad/30 rounded-lg text-bad text-sm">
 {error}
 </div>
 )}

 <div className="flex gap-3">
 <Button
 variant="outline"
 onClick={() => navigate(-1)}
 className="flex-1"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Cancel
 </Button>
 <Button
 onClick={startSetup}
 disabled={loading}
 className="flex-1 bg-ok hover:opacity-90 text-on-brand"
 >
 {loading ? <Loader2 className="w-5 h-5 animate-spin" /> :'Start Setup'}
 </Button>
 </div>
 </div>
 </div>
 </div>
 )
 }

 // Setup step (QR code)
 if (step ==='setup') {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-lg">
 <div className="bg-surface rounded-2xl shadow-xl p-8">
 <div className="text-center mb-6">
 <h1 className="text-xl font-semibold text-primary mb-2">
 Scan QR Code
 </h1>
 <p className="text-secondary">
 Scan this code with your authenticator app
 </p>
 </div>

 {/* QR Code */}
 <div className="flex justify-center mb-6">
 <div className="p-4 bg-surface rounded-xl shadow-inner">
 <img
 src={`data:image/png;base64,${setupData?.qr_code}`}
 alt="QR Code"
 className="w-48 h-48"
 />
 </div>
 </div>

 {/* Manual entry key */}
 <div className="mb-6">
 <p className="text-sm text-secondary mb-2 text-center">
 Or enter this key manually:
 </p>
 <div className="flex items-center gap-2 p-3 bg-surface-alt rounded-lg">
 <code className="flex-1 text-center font-mono text-sm tracking-wider">
 {setupData?.manual_entry_key}
 </code>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => copyToClipboard(setupData?.manual_entry_key ||'','key')}
 >
 {copiedKey ? (
                  <CheckCircle2 className="w-4 h-4 text-ok" />
 ) : (
 <Copy className="w-4 h-4" />
 )}
 </Button>
 </div>
 </div>

 <Button
 onClick={() => setStep('verify')}
 className="w-full bg-brand hover:bg-brand-hover text-on-brand"
 >
 I've Scanned the Code
 </Button>
 </div>
 </div>
 </div>
 )
 }

 // Verify step
 if (step ==='verify') {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8">
 <div className="text-center mb-6">
 <div className="w-16 h-16 bg-brand rounded-2xl flex items-center justify-center mx-auto mb-4">
 <Key className="w-8 h-8 text-on-brand" />
 </div>
 <h1 className="text-xl font-semibold text-primary mb-2">
 Enter Verification Code
 </h1>
 <p className="text-secondary">
 Enter the 6-digit code from your authenticator app
 </p>
 </div>

 {error && (
 <div className="mb-4 p-4 bg-bad/10 border border-bad/30 rounded-lg text-bad text-sm">
 {error}
 </div>
 )}

 <div className="mb-6">
 <input
 type="text"
 value={verificationCode}
 onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g,'').slice(0, 6))}
 placeholder="000000"
 className="w-full text-center text-3xl font-mono tracking-[0.5em] px-4 py-4 border border-soft rounded-xl bg-surface text-primary focus:ring-2 focus:ring-brand focus:border-transparent"
 maxLength={6}
 autoFocus
 />
 </div>

 <div className="flex gap-3">
 <Button
 variant="outline"
 onClick={() => setStep('setup')}
 className="flex-1"
 >
 Back
 </Button>
 <Button
 onClick={verifyCode}
 disabled={loading || verificationCode.length !== 6}
 className="flex-1 bg-brand hover:bg-brand-hover text-on-brand"
 >
 {loading ? <Loader2 className="w-5 h-5 animate-spin" /> :'Verify'}
 </Button>
 </div>
 </div>
 </div>
 </div>
 )
 }

 // Backup codes step
 if (step ==='backup') {
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-lg">
 <div className="bg-surface rounded-2xl shadow-xl p-8">
 <div className="text-center mb-6">
 <div className="w-16 h-16 bg-warn rounded-2xl flex items-center justify-center mx-auto mb-4">
 <AlertTriangle className="w-8 h-8 text-on-brand" />
 </div>
 <h1 className="text-xl font-semibold text-primary mb-2">
 Save Your Backup Codes
 </h1>
 <p className="text-secondary">
 These codes can be used to access your account if you lose your phone.
 <strong className="text-primary"> Save them somewhere safe!</strong>
 </p>
 </div>

 <div className="bg-surface-alt rounded-xl p-4 mb-4">
 <div className="grid grid-cols-2 gap-2">
 {setupData?.backup_codes.map((code, index) => (
 <div
 key={index}
 className="font-mono text-sm bg-surface px-3 py-2 rounded-lg text-center"
 >
 {code}
 </div>
 ))}
 </div>
 </div>

 <Button
 variant="outline"
 onClick={() => copyToClipboard(formatBackupCodes(),'codes')}
 className="w-full mb-6"
 >
 {copiedCodes ? (
 <>
 <CheckCircle2 className="w-4 h-4 mr-2 text-ok" />
 Copied!
 </>
 ) : (
 <>
 <Copy className="w-4 h-4 mr-2" />
 Copy All Codes
 </>
 )}
 </Button>

 <div className="p-4 bg-warn/10 border border-warn/30 rounded-lg mb-6">
 <p className="text-sm text-warn">
 ⚠️ Each backup code can only be used once. Store them securely and don't share them with anyone.
 </p>
 </div>

 <Button
 onClick={() => setStep('complete')}
 className="w-full bg-ok hover:opacity-90 text-on-brand"
 >
 I've Saved My Codes
 </Button>
 </div>
 </div>
 </div>
 )
 }

 // Complete step
 return (
 <div className="min-h-screen flex items-center justify-center bg-default p-4">
 <div className="w-full max-w-md">
 <div className="bg-surface rounded-2xl shadow-xl p-8 text-center">
 <div className="w-20 h-20 bg-ok/10 rounded-full flex items-center justify-center mx-auto mb-6">
 <CheckCircle2 className="w-10 h-10 text-ok" />
 </div>
 <h1 className="text-xl font-semibold text-primary mb-4">
 MFA Enabled! 🎉
 </h1>
 <p className="text-secondary mb-6">
 Your account is now protected with two-factor authentication. You'll need your authenticator app to sign in.
 </p>
 <Button
 onClick={() => navigate(DEFAULT_APP_HOME)}
 className="w-full bg-brand hover:bg-brand-hover text-on-brand"
 >
 Go to Dashboard
 </Button>
 </div>
 </div>
 </div>
 )
}

